import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  Plus, 
  X, 
  Globe, 
  Lock, 
  Upload, 
  FileText, 
  Loader2,
  AlertCircle,
  CheckCircle2,
  Sparkles
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Alert,
  AlertDescription,
} from "../components/ui/alert";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateJob() {
  const navigate = useNavigate();
  const { user, requireEmail } = useUser();
  const [loading, setLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState(null); // null | 'manual' | 'document'
  const [uploading, setUploading] = useState(false);
  const [extractedFromDoc, setExtractedFromDoc] = useState(false);
  const [sourceFilename, setSourceFilename] = useState("");
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    required_skills: [],
    optional_skills: [],
    min_years_experience: 0,
    education_level: "",
    is_public: false,
  });
  const [skillInput, setSkillInput] = useState("");
  const [optionalSkillInput, setOptionalSkillInput] = useState("");
  
  const fileInputRef = useRef(null);
  const pendingSubmitRef = useRef(false);
  const formDataRef = useRef(formData);
  
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    if (user?.email && pendingSubmitRef.current) {
      pendingSubmitRef.current = false;
      setTimeout(() => {
        submitJobWithEmail(user.email);
      }, 200);
    }
  }, [user?.email]);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const validExtensions = ['.pdf', '.docx'];
    const hasValidExt = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!validTypes.includes(file.type) && !hasValidExt) {
      toast.error("Formato no soportado. Usá archivos PDF o Word (.docx)");
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo es muy grande (máximo 10MB)");
      return;
    }
    
    setUploading(true);
    setUploadMode('document');
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/jobs/extract-from-document`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const extracted = response.data;
      
      // Build description from responsibilities if needed
      let description = extracted.description || "";
      if (extracted.responsibilities?.length > 0) {
        description += description ? "\n\nResponsabilidades:\n" : "Responsabilidades:\n";
        description += extracted.responsibilities.map(r => `• ${r}`).join("\n");
      }
      if (extracted.notes) {
        description += description ? `\n\nNotas: ${extracted.notes}` : extracted.notes;
      }
      
      // Update form with extracted data
      setFormData({
        title: extracted.title || "",
        description: description,
        required_skills: extracted.required_skills || [],
        optional_skills: extracted.optional_skills || [],
        min_years_experience: extracted.min_years_experience || 0,
        education_level: extracted.education_level || "",
        is_public: false,
      });
      
      setExtractedFromDoc(true);
      setSourceFilename(file.name);
      toast.success("Perfil extraído del documento. Revisá y ajustá antes de continuar.");
      
    } catch (error) {
      console.error("Error uploading document:", error);
      const detail = error.response?.data?.detail || "Error al procesar el documento";
      toast.error(detail);
      setUploadMode(null);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleAddSkill = (type) => {
    const input = type === "required" ? skillInput : optionalSkillInput;
    const trimmed = input.trim();
    
    if (!trimmed) return;
    
    const skills = type === "required" ? formData.required_skills : formData.optional_skills;
    if (skills.includes(trimmed)) {
      toast.error("Habilidad ya agregada");
      return;
    }
    
    setFormData({
      ...formData,
      [type === "required" ? "required_skills" : "optional_skills"]: [...skills, trimmed],
    });
    
    if (type === "required") {
      setSkillInput("");
    } else {
      setOptionalSkillInput("");
    }
  };

  const handleRemoveSkill = (type, skill) => {
    const key = type === "required" ? "required_skills" : "optional_skills";
    setFormData({
      ...formData,
      [key]: formData[key].filter((s) => s !== skill),
    });
  };

  const submitJobWithEmail = async (email) => {
    setLoading(true);
    try {
      const currentFormData = formDataRef.current;
      const payload = {
        ...currentFormData,
        user_email: email,
      };
      const response = await axios.post(`${API}/jobs`, payload);
      toast.success("¡Puesto creado exitosamente!");
      navigate(`/jobs/${response.data.id}`);
    } catch (error) {
      console.error("Error creating job:", error);
      toast.error(error.response?.data?.detail || "Error al crear el puesto");
    } finally {
      setLoading(false);
    }
  };

  const submitJob = async () => {
    if (user?.email) {
      await submitJobWithEmail(user.email);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error("Por favor ingresá un título");
      return;
    }
    
    if (formData.required_skills.length === 0) {
      toast.error("Por favor agregá al menos una habilidad requerida");
      return;
    }
    
    if (user?.email) {
      submitJob();
    } else {
      pendingSubmitRef.current = true;
      requireEmail(() => {});
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      required_skills: [],
      optional_skills: [],
      min_years_experience: 0,
      education_level: "",
      is_public: false,
    });
    setUploadMode(null);
    setExtractedFromDoc(false);
    setSourceFilename("");
  };

  // Loading state while extracting from document
  if (uploading) {
    return (
      <div className="space-y-6" data-testid="create-job-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            disabled
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              Analizando Documento
            </h1>
            <p className="text-muted-foreground mt-1">
              Extrayendo información del perfil...
            </p>
          </div>
        </div>

        {/* Loading Card */}
        <Card className="max-w-lg mx-auto">
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-6">
              {/* Animated Icon */}
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 rounded-2xl bg-primary/20 animate-ping" />
                <div className="relative w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-primary animate-pulse" />
                </div>
              </div>
              
              {/* Loading Text */}
              <div className="space-y-2">
                <h3 className="font-heading font-semibold text-lg">
                  Analizando con IA...
                </h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  Estamos extrayendo el título, habilidades, requisitos y más información del documento.
                </p>
              </div>
              
              {/* Progress indicator */}
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">
                  Esto puede tomar unos segundos...
                </span>
              </div>
              
              {/* Steps */}
              <div className="text-left max-w-xs mx-auto space-y-2 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>Documento recibido</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="text-muted-foreground">Extrayendo texto...</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground/50">
                  <div className="w-4 h-4 rounded-full border-2 border-muted" />
                  <span>Analizando requisitos</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground/50">
                  <div className="w-4 h-4 rounded-full border-2 border-muted" />
                  <span>Generando perfil</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Initial mode selection
  if (uploadMode === null) {
    return (
      <div className="space-y-6" data-testid="create-job-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              Nuevo Perfil de Puesto
            </h1>
            <p className="text-muted-foreground mt-1">
              ¿Cómo querés crear el perfil?
            </p>
          </div>
        </div>

        {/* Mode Selection */}
        <div className="grid gap-4 md:grid-cols-2 max-w-3xl">
          {/* Upload Document Option */}
          <Card 
            className="cursor-pointer hover:border-primary hover:shadow-md transition-all group"
            onClick={() => fileInputRef.current?.click()}
          >
            <CardContent className="pt-6 text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto group-hover:bg-primary/20 transition-colors">
                {uploading ? (
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                ) : (
                  <Upload className="w-8 h-8 text-primary" />
                )}
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">
                  Subir perfil del puesto
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  PDF o Word (.docx)
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Usá un perfil existente y ajustalo antes de analizar candidatos.
              </p>
              <div className="flex items-center justify-center gap-2 text-xs text-primary">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Extracción automática con IA</span>
              </div>
            </CardContent>
          </Card>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileUpload}
            className="hidden"
          />

          {/* Manual Creation Option */}
          <Card 
            className="cursor-pointer hover:border-primary hover:shadow-md transition-all group"
            onClick={() => setUploadMode('manual')}
          >
            <CardContent className="pt-6 text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto group-hover:bg-muted/80 transition-colors">
                <FileText className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">
                  Crear manualmente
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Completá el formulario
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Definí los requisitos del puesto paso a paso.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Form view (manual or after document extraction)
  return (
    <div className="space-y-6" data-testid="create-job-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            if (extractedFromDoc || formData.title) {
              if (window.confirm("¿Seguro que querés salir? Los cambios no guardados se perderán.")) {
                navigate(-1);
              }
            } else {
              navigate(-1);
            }
          }}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            {extractedFromDoc ? "Revisar Perfil de Puesto" : "Nuevo Perfil de Puesto"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {extractedFromDoc 
              ? "Revisá y ajustá antes de continuar" 
              : "Definí los requisitos para el puesto"
            }
          </p>
        </div>
      </div>

      {/* Extracted from document alert */}
      {extractedFromDoc && (
        <Alert className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700 dark:text-emerald-300">
            <span className="font-medium">Perfil generado a partir del documento.</span>
            <span className="block text-sm mt-0.5 opacity-80">
              Archivo: {sourceFilename} — Revisá y ajustá antes de continuar.
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="pt-6 space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Título del Puesto *</Label>
              <Input
                id="title"
                placeholder="Ej: Desarrollador Full Stack Senior"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                data-testid="job-title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Descripción y Responsabilidades</Label>
              <Textarea
                id="description"
                placeholder="Describí el rol, responsabilidades principales y contexto del equipo..."
                rows={6}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                data-testid="job-description-input"
              />
            </div>

            {/* Required Skills */}
            <div className="space-y-2">
              <Label>Habilidades Requeridas *</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Agregar habilidad..."
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSkill("required");
                    }
                  }}
                  data-testid="required-skill-input"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleAddSkill("required")}
                  data-testid="add-required-skill-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.required_skills.map((skill) => (
                  <Badge
                    key={skill}
                    variant="default"
                    className="flex items-center gap-1 px-3 py-1"
                    data-testid={`required-skill-${skill}`}
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => handleRemoveSkill("required", skill)}
                      className="hover:text-destructive ml-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {formData.required_skills.length === 0 && (
                  <span className="text-sm text-muted-foreground italic">
                    Agregá las habilidades obligatorias para el puesto
                  </span>
                )}
              </div>
            </div>

            {/* Optional Skills */}
            <div className="space-y-2">
              <Label>Habilidades Deseables (Opcionales)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Agregar habilidad..."
                  value={optionalSkillInput}
                  onChange={(e) => setOptionalSkillInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSkill("optional");
                    }
                  }}
                  data-testid="optional-skill-input"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleAddSkill("optional")}
                  data-testid="add-optional-skill-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.optional_skills.map((skill) => (
                  <Badge
                    key={skill}
                    variant="outline"
                    className="flex items-center gap-1 px-3 py-1"
                    data-testid={`optional-skill-${skill}`}
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => handleRemoveSkill("optional", skill)}
                      className="hover:text-destructive ml-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Public Job Toggle */}
            <div className="space-y-3 p-4 bg-muted/50 rounded-lg border">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="is_public"
                  checked={formData.is_public}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_public: checked })
                  }
                  data-testid="is-public-checkbox"
                />
                <div className="space-y-1">
                  <Label 
                    htmlFor="is_public" 
                    className="flex items-center gap-2 cursor-pointer font-medium"
                  >
                    {formData.is_public ? (
                      <Globe className="w-4 h-4 text-green-600" />
                    ) : (
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    )}
                    Publicar en Portal de Candidatos
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {formData.is_public 
                      ? "Este puesto será visible para candidatos que buscan empleo"
                      : "Solo vos podrás ver este puesto. Los candidatos no podrán encontrarlo."
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Experience & Education */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="experience">Años de Experiencia Mínimos</Label>
                <Select
                  value={formData.min_years_experience.toString()}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      min_years_experience: parseInt(value),
                    })
                  }
                >
                  <SelectTrigger data-testid="experience-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Sin mínimo</SelectItem>
                    <SelectItem value="1">1+ años</SelectItem>
                    <SelectItem value="2">2+ años</SelectItem>
                    <SelectItem value="3">3+ años</SelectItem>
                    <SelectItem value="5">5+ años</SelectItem>
                    <SelectItem value="7">7+ años</SelectItem>
                    <SelectItem value="10">10+ años</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="education">Nivel de Educación</Label>
                <Select
                  value={formData.education_level}
                  onValueChange={(value) =>
                    setFormData({ ...formData, education_level: value })
                  }
                >
                  <SelectTrigger data-testid="education-select">
                    <SelectValue placeholder="Seleccionar (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No especificado</SelectItem>
                    <SelectItem value="High School">Secundaria</SelectItem>
                    <SelectItem value="Associate">Técnico</SelectItem>
                    <SelectItem value="Bachelor">Licenciatura / Grado</SelectItem>
                    <SelectItem value="Master">Maestría</SelectItem>
                    <SelectItem value="PhD">Doctorado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="ghost"
                onClick={resetForm}
                className="text-muted-foreground"
              >
                {extractedFromDoc ? "Subir otro documento" : "Empezar de nuevo"}
              </Button>
              
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  data-testid="cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="rounded-full"
                  disabled={loading}
                  data-testid="submit-job-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    "Crear Puesto"
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
